import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import i18n from '../../i18n';

const STATUS_ABBREVIATIONS = {
    'partially implemented': 'PI',
    'not implemented': 'NI',
    'fully implemented': 'FI',
    'largely implemented': 'LI',
    'in practice': 'IP',
};

const headingLookup = {};

const GOVERN_SUBCATEGORY_ORDER = [
    'Organizational Context',
    'Risk Management Strategy',
    'Roles, Responsibilities, and Authorities',
    'Policy',
    'Oversight',
    'Cybersecurity Supply Chain Risk Management',
];

const SUBCATEGORY_HEADING_FALLBACKS = {
    'identity management, authentication, and access control':
        'Identity Management, Authentication, and Access Control (PR.AA): Access to physical and logical assets is limited to authorized users, services, and hardware and managed commensurate with the assessed risk of unauthorized access',
    'technology infrastructure resilience':
        "Technology Infrastructure Resilience (PR.IR): Security architectures are managed with the organization's risk strategy to protect asset confidentiality, integrity, and availability, and organizational resilience",
    'incident recovery communication':
        'Incident Recovery Plan Execution (RC.RP): Restoration activities are performed to ensure operational availability of systems and services affected by cybersecurity incidents',
    'incident recovery plan execution':
        'Incident Recovery Communication (RC.CO): Restoration activities are coordinated with internal and external parties',
};

const formatStatusValue = (value) => {
    const raw = typeof value === 'string' ? value.trim() : value?.toString?.().trim?.() || '';
    if (!raw) return '';
    return STATUS_ABBREVIATIONS[raw.toLowerCase()] || raw;
};

const buildHeadingKey = (sectionKey, categoryCode) => {
    if (!sectionKey || !categoryCode) return null;
    const cleanedCode = String(categoryCode).replace(/:$/, '').trim();
    if (!cleanedCode) return null;
    return `${sectionKey}_${cleanedCode}`;
};

const storeHeading = (sectionKey, categoryCode, headingText) => {
    const key = buildHeadingKey(sectionKey, categoryCode);
    if (!key) return;
    const normalizedHeading = (headingText || '').toString().trim();
    if (!normalizedHeading) return;
    if (!headingLookup[key]) {
        headingLookup[key] = normalizedHeading;
    }
};

const deriveCategoryCode = (subcat = {}, rows = []) => {
    const directCode = subcat?.categoryCode || subcat?.category_code || subcat?.categoryId;
    if (directCode) return String(directCode).replace(/:$/, '');

    const question = Array.isArray(subcat?.questions) && subcat.questions.length ? subcat.questions[0] : null;
    const candidateId =
        question?.category_id ||
        question?.categoryid ||
        rows?.[0]?.categoryId ||
        '';
    if (!candidateId) return '';

    const cleaned = String(candidateId).replace(/:$/, '');
    const dashIndex = cleaned.indexOf('-');
    return dashIndex > 0 ? cleaned.slice(0, dashIndex) : cleaned;
};

export function handleEvidencePdfDownload(
    fetchData,
    setShowEvidenceDropdown,
    contentMap = {},
    frameworkData = null,
    ownerEmail = '',
    sectionName = '',
    scopeParam = '',
    userData = null
) {
    let tocPageNumber = 0;
    const evidenceData = [];
    let sectionOrder = Object.keys(fetchData?.categories || {});
    if (sectionOrder.length === 0) {
        sectionOrder = ['SMART MAP CONTROLS'];
    }

    const sectionConfig = {};
    const colors = [
        [246, 244, 150], [58, 166, 229], [100, 45, 134],
        [242, 198, 0], [209, 0, 31], [0, 150, 102]
    ];
    const texts = [
        [0, 0, 0], [255, 255, 255], [255, 255, 255],
        [0, 0, 0], [255, 255, 255], [255, 255, 255]
    ];
    const alts = [
        [250, 249, 237], [235, 248, 255], [242, 235, 247],
        [255, 248, 204], [252, 229, 229], [219, 244, 233]
    ];

    sectionOrder.forEach((sec, idx) => {
        const cIdx = idx % colors.length;
        sectionConfig[sec] = {
            title: sec,
            firstColumnFill: colors[cIdx],
            firstColumnText: texts[cIdx],
            priorityFill: [224, 224, 224],
            priorityText: [0, 0, 0],
            alternateFill: alts[cIdx],
        };
    });

    const sections = sectionOrder.reduce((acc, key) => {
        acc[key] = [];
        return acc;
    }, {});

    const formatDateValue = (value) => {
        if (value === undefined || value === null) return null;

        const coerceDate = (candidate) => {
            if (!(candidate instanceof Date)) return null;
            if (Number.isNaN(candidate.getTime())) return null;
            const y = candidate.getFullYear();
            const m = String(candidate.getMonth() + 1).padStart(2, '0');
            const d = String(candidate.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        if (value instanceof Date) {
            return coerceDate(value);
        }

        if (typeof value === 'number') {
            return coerceDate(new Date(value));
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const parsed = new Date(trimmed);
            if (!Number.isNaN(parsed.getTime())) {
                return coerceDate(parsed);
            }
            return trimmed;
        }

        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return coerceDate(parsed);
        }

        return null;
    };

    const normalizeVerifiedValue = (value) => {
        if (value === undefined || value === null) return null;

        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        if (typeof value === 'number') {
            return value === 1 ? 'Yes' : 'No';
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const lower = trimmed.toLowerCase();
            if (['yes', 'true', 'verified', 'y', '1', 'checked'].includes(lower)) return 'Yes';
            if (['no', 'false', 'not verified', 'n', '0', 'unchecked'].includes(lower)) return 'No';
            return 'No';
        }

        return 'No';
    };

    const pickVerifiedValue = (...values) => {
        for (const value of values) {
            const normalized = normalizeVerifiedValue(value);
            if (normalized) return normalized;
        }
        return null;
    };

    const downloadDateString = formatDateValue(new Date()) || 'Not Available';
    const ownerEmailValue = userData?.email || ownerEmail?.trim?.() || 'Not Available';
    const scopeValue = scopeParam?.trim() || fetchData?.scope || fetchData?.assessment?.scope || 'Not Specified';


    const resolveSectionKey = (categoryName = '') => {
        return categoryName || 'SMART MAP CONTROLS';
    };

    if (frameworkData && typeof frameworkData === 'object') {
        Object.entries(frameworkData).forEach(([rawSectionKey, sectionValue]) => {
            const sectionKey = resolveSectionKey(rawSectionKey) || resolveSectionKey(sectionValue?.category) || rawSectionKey;

            const items = Array.isArray(sectionValue?.items)
                ? sectionValue.items
                : Array.isArray(sectionValue?.subcategories)
                    ? sectionValue.subcategories
                    : Array.isArray(sectionValue)
                        ? sectionValue
                        : [];

            items.forEach((item) => {
                const categoryCode = deriveCategoryCode(item, item?.questions);
                const headingText = item?.heading || item?.content || '';
                storeHeading(sectionKey, categoryCode, headingText);
            });
        });
    }

    Object.entries(fetchData?.categories || {}).forEach(([categoryName, categoryObj]) => {
        const sectionKey = resolveSectionKey(categoryName);

        const rawSubcategories = Array.isArray(categoryObj?.subcategories)
            ? [...categoryObj.subcategories]
            : [];

        const subcategories =
            sectionKey === 'GOVERN'
                ? rawSubcategories.sort((a, b) => {
                    const nameA = a?.subcategory_name || a?.subcategory || a?.task || '';
                    const nameB = b?.subcategory_name || b?.subcategory || b?.task || '';
                    const indexA = GOVERN_SUBCATEGORY_ORDER.indexOf(nameA);
                    const indexB = GOVERN_SUBCATEGORY_ORDER.indexOf(nameB);

                    if (indexA === -1 && indexB === -1) return nameA.localeCompare(nameB);
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                })
                : rawSubcategories;

        subcategories.forEach((subcat) => {
            const rows = [];
            subcat.questions?.forEach((q) => {
                const baseRow = {
                    "Category ID": q.category_id,
                    "Category": q.task,
                    "Sub Category": q.question,
                    "Priority": q.priority,
                    "Result": q.status,
                    "Remark": q.notes || "",
                };

                const questionVerifiedFallback = pickVerifiedValue(
                    q.verifiedEvidence,
                    q.verified_evidence,
                    q.verified,
                    q.authorised,
                    q.is_verified,
                    q.verified_status,
                    q.evidence_verified
                ) ?? 'No';

                const questionLevelDate = formatDateValue(
                    q.evidence_date ||
                    q.collection_date ||
                    q.collected_on ||
                    q.uploaded_date ||
                    q.date
                );

                let evidenceFiles = ['Not Uploaded'];
                let verifiedValues = questionVerifiedFallback ? [questionVerifiedFallback] : [];
                let evidenceDates = questionLevelDate ? [questionLevelDate] : [];
                if (Array.isArray(q.evidence) && q.evidence.length > 0) {
                    const incomingFiles = [];
                    const incomingVerified = [];
                    const incomingDates = [];

                    q.evidence.forEach((ev) => {
                        if (ev.s3_path) {
                            incomingFiles.push(ev.file_name?.trim() !== '' ? ev.file_name : 'Unnamed File');
                        }
                        else if (typeof ev.file_name === 'string' && ev.file_name.trim() !== '') {
                            incomingFiles.push(ev.file_name.trim());
                        }
                        else if (typeof ev.filename === 'string' && ev.filename.trim() !== '') {
                            incomingFiles.push(ev.filename.trim());
                        }
                        else {
                            incomingFiles.push('Not Uploaded');
                        }

                        const verifiedDisplay = (pickVerifiedValue(
                            ev.verified_evidence,
                            ev.verified,
                            ev.authorised,
                            ev.is_verified,
                            ev.status,
                            ev.result,
                            ev.verifiedEvidence,
                            ev.isVerified,
                            ev.isVerifiedEvidence,
                            ev.verified_status
                        )) ?? questionVerifiedFallback;
                        incomingVerified.push(verifiedDisplay || 'No');

                        const formattedDate =
                            formatDateValue(ev.evidence_date || ev.collection_date || ev.collected_on || ev.uploaded_date || ev.date);
                        if (formattedDate) {
                            incomingDates.push(formattedDate);
                        }
                    });

                    evidenceFiles = incomingFiles.length ? incomingFiles : ['Not Uploaded'];

                    const cleanedVerified = incomingVerified
                        .map((val) => (typeof val === 'string' ? val.trim() : val))
                        .filter((val) => val && val !== 'Not Available');
                    if (cleanedVerified.length) {
                        verifiedValues = cleanedVerified;
                    } else if (questionVerifiedFallback) {
                        verifiedValues = [questionVerifiedFallback];
                    } else {
                        verifiedValues = ['No'];
                    }

                    const cleanedDates = incomingDates
                        .map((val) => (typeof val === 'string' ? val.trim() : val))
                        .filter((val) => val && val !== 'Not Available');
                    if (cleanedDates.length) {
                        evidenceDates = cleanedDates;
                    } else if (questionLevelDate) {
                        evidenceDates = [questionLevelDate];
                    } else {
                        evidenceDates = [downloadDateString];
                    }

                    evidenceData.push({
                        ...baseRow,
                        "Filename": evidenceFiles.join(', '),
                        "Verified Evidence": verifiedValues.join(', '),
                    });
                } else {
                    if (!verifiedValues.length) {
                        verifiedValues = questionVerifiedFallback ? [questionVerifiedFallback] : ['No'];
                    }
                    evidenceDates = evidenceDates.length ? evidenceDates : (questionLevelDate ? [questionLevelDate] : [downloadDateString]);
                    evidenceData.push({
                        ...baseRow,
                        "Filename": 'Not Uploaded',
                        "Verified Evidence": verifiedValues.join(', ') || 'No',
                    });
                }

                if (!evidenceDates.length) {
                    evidenceDates = [downloadDateString];
                }

                rows.push({
                    categoryId: q.category_id || q.categoryid || '',
                    question: q.question || '',
                    priority: formatStatusValue(q.priority || ''),
                    status: formatStatusValue(q.status || q.result || ''),
                    remark: q.notes || q.remark || '',
                    evidenceFiles,
                    verifiedStatuses: verifiedValues,
                    evidenceDates,
                });
            });

            if (sectionKey && sections[sectionKey] && rows.length) {
                const subcategoryName = subcat.subcategory_name || subcat.subcategory || 'Category';
                const description = subcat.subcategory_description || subcat.description || subcat.details || '';
                const categoryCode = deriveCategoryCode(subcat, rows);
                const normalizedName = subcategoryName?.toString?.().trim().toLowerCase() || '';
                const fallbackHeading = SUBCATEGORY_HEADING_FALLBACKS[normalizedName] || '';
                const heading =
                    subcat.heading ||
                    subcat.content ||
                    headingLookup[buildHeadingKey(sectionKey, categoryCode)] ||
                    fallbackHeading ||
                    '';

                storeHeading(sectionKey, categoryCode, heading || fallbackHeading);

                sections[sectionKey].push({
                    name: subcategoryName,
                    description,
                    heading,
                    categoryCode,
                    rows,
                });
            }
        });
    });

    if (evidenceData.length > 0) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        // --- Cover Page (Page 1) ---
        // Add Logo
        try {
            const coverLogo = new Image();
            coverLogo.src = '/img/CARA_DarkTheme.png';
            const logoWidth = 70; 
            const logoHeight = 25;
            const pageWidth = doc.internal.pageSize.getWidth();
            const xPos = (pageWidth - logoWidth) / 2;
            doc.addImage(coverLogo, 'PNG', xPos, 40, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Logo not found", e);
            doc.setFontSize(28);
            doc.setTextColor(27, 169, 218); // CARA Blue
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.text("CARA", pageWidth / 2, 60, { align: 'center' });
        }

        // Cover Table Data
        const reportVersion = "V1.0";
        const dateStr = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });


        const coverBody = [
            ["0", "Assessment Name", scopeValue],
            ["1", "Report Version", reportVersion],
            ["2", "Prepared by", "CARAgrc"],
            ["3", "Generated by", (userData?.username && userData?.username.length > 50) ? (localStorage.getItem("username") || userData?.username) : (userData?.username || "User")],
            ["4", "Submitted by", (() => {
                const isEncrypted = (str) => str && str.startsWith("gAAAAA");
                const first = !isEncrypted(userData?.first_name) ? (userData?.first_name || localStorage.getItem("first_name") || "") : (localStorage.getItem("first_name") || "");
                const last = !isEncrypted(userData?.last_name) ? (userData?.last_name || localStorage.getItem("last_name") || "") : (localStorage.getItem("last_name") || "");
                const fullName = `${first} ${last}`.trim();
                return fullName || localStorage.getItem("username") || "User";
            })()],
            ["5", "Email", ownerEmailValue],
            ["6", "Date", dateStr]
        ];


        autoTable(doc, {
            startY: 100,
            head: [['Sr', 'Report Title', 'Smart Map Evidence']],
            body: coverBody,
            theme: 'grid',
            styles: {
                fontSize: 10,
                cellPadding: 4,
                valign: 'middle',
                lineColor: [0, 0, 0], // Black border
                lineWidth: 0.1,
            },
            headStyles: {
                fillColor: [27, 169, 218], // CARA Blue
                textColor: [255, 255, 255], // White text
                fontStyle: 'bold',
                halign: 'left'
            },
            columnStyles: {
                0: { cellWidth: 20, fillColor: [27, 169, 218], textColor: [255, 255, 255], fontStyle: 'bold' }, // Sr col
                1: { cellWidth: 60, fillColor: [27, 169, 218], textColor: [255, 255, 255], fontStyle: 'bold' }, // Report Title col
                2: { cellWidth: 'auto', textColor: [0, 0, 0] } // Value col (Auto fits remaining)
            },
            didParseCell: (data) => {
                // For body rows, ensure first two columns have Blue bg
                if (data.section === 'body') {
                    if (data.column.index === 0 || data.column.index === 1) {
                        data.cell.styles.fillColor = [27, 169, 218];
                        data.cell.styles.textColor = [255, 255, 255];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });

        // --- Page 2: Title Page ---
        doc.addPage();
        const pageWidth2 = doc.internal.pageSize.getWidth();

        // 1. Report Title
        doc.setFontSize(16);
        doc.setTextColor(0, 128, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(sectionName || "Evidence Report", pageWidth2 / 2, 60, { align: "center" });

        // 2. Framework Title
        doc.setFontSize(24);
        doc.setTextColor(0, 102, 255);
        doc.text("Smart Map Controls", pageWidth2 / 2, 130, { align: "center" });

        // 3. Logo
        try {
            const titleLogo = new Image();
            titleLogo.src = '/img/CARA_DarkTheme.png';
            const imgW = 100;
            const imgH = 50;
            doc.addImage(titleLogo, 'PNG', (pageWidth2 - imgW) / 2, 160, imgW, imgH);
        } catch (e) { }

        // 4. Footer
        doc.setFontSize(14);
        doc.setTextColor(0, 128, 0);
        doc.text("CARA PLATFORM", pageWidth2 / 2, 270, { align: "center" });

        // Reset
        doc.setTextColor(0, 0, 0);

        // --- Page 3: Content ---

        // --- Page 2: Disclaimer ---
        doc.addPage();
        const disclaimerMarginLeft = 22;
        const disclaimerMarginRight = 32;
        const disclaimerMaxWidth = pageWidth - disclaimerMarginLeft - disclaimerMarginRight;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text('DISCLAIMER OF WARRANTIES AND LIMITATION OF LIABILITIES', disclaimerMarginLeft, 40);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text('Copyright 2018-23 CyberInsurify Labs.', disclaimerMarginLeft, 56);
        doc.text('CyberInsurify.com is a product of CyberInsurify Labs.', disclaimerMarginLeft, 63);

        // Cara logo between "CyberInsurify.com is a product of CyberInsurify Labs." and "NO WARRANTY..."
        try {
            const caraLogo = new Image();
            caraLogo.src = '/img/cyberinsurify_logo.png';
            doc.addImage(caraLogo, 'PNG', disclaimerMarginLeft, 75, 60, 22);
        } catch { }

        let disclaimerY = 105; // Adjusted to account for logo
        const lineGap = 5.8;

        const writeParagraph = (text, options = {}) => {
            const { bold = false, extraGap = lineGap } = options;
            const fontStyle = bold ? 'bold' : 'normal';
            doc.setFont('helvetica', fontStyle);
            const lines = doc.splitTextToSize(text, disclaimerMaxWidth);
            lines.forEach((line) => {
                doc.text(line, disclaimerMarginLeft, disclaimerY);
                disclaimerY += lineGap;
            });
            disclaimerY += extraGap - lineGap;
        };

        writeParagraph('NO WARRANTY: THIS CYBERINSURIFY LABS MATERIAL IS FURNISHED ON AN AS-IS BASIS. CYBERINSURIFY LABS MAKES NO WARRANTIES OF ANY KIND, EITHER EXPRESSED OR IMPLIED, AS TO ANY MATTER INCLUDING, BUT NOT LIMITED TO, WARRANTY OF FITNESS FOR PURPOSE OR MERCHANTABILITY, EXCLUSIVITY, OR RESULTS OBTAINED FROM USE OF THE MATERIAL. CYBERINSURIFY LABS DOES NOT MAKE ANY WARRANTY OF ANY KIND WITH RESPECT TO FREEDOM FROM PATENT, TRADEMARK, OR COPYRIGHT INFRINGEMENT.');

        writeParagraph('Internal use: Permission to reproduce this material and to prepare derivative works from this material for use inside your organization is granted, provided the copyright and No Warranty statements are included with all reproductions and derivative works.', { extraGap: lineGap + 2 });

        writeParagraph('External use: This material may be reproduced in its entirety, without modification, and freely distributed in written or electronic form without requesting formal permission. Permission is required for any other external and/or commercial use. Requests for such permission should be directed to info@cyberinsurify.com.', { extraGap: lineGap + 2 });

        writeParagraph('CyberInsurify Labs. PREPARED THIS REPORT.', { extraGap: lineGap + 6 });

        doc.setFont('helvetica', 'bold');
        doc.text('NOTE', disclaimerMarginLeft, disclaimerY);
        disclaimerY += lineGap + 2;

        doc.setFont('helvetica', 'normal');
        writeParagraph('For further information about CyberInsurify Labs, email us info@cyberinsurify.com. Copyright 2018-23 CyberInsurify Labs. All rights reserved.', { extraGap: 0 });



        // --- Page 3: About CyberInsurify and Evidences Details ---
        doc.addPage();
        doc.setPage(doc.internal.getNumberOfPages());
        const aboutMarginLeft = 22;
        const aboutMarginRight = 36;
        const aboutMaxWidth = pageWidth - aboutMarginLeft - aboutMarginRight;

        // Cara logo before "About CyberInsurify"
        try {
            const aboutLogo = new Image();
            aboutLogo.src = '/img/cyberinsurify_logo.png';
            doc.addImage(aboutLogo, 'PNG', aboutMarginLeft, 30, 60, 22);
        } catch { }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(32);
        doc.setTextColor(0, 0, 0);
        doc.text('About CyberInsurify', aboutMarginLeft, 82);

        let aboutY = 98; // Increased from 85 to prevent overlap with 32pt title
        const paragraphFontSize = 11;
        const paragraphLineHeight = 5; // Reduced from 5.2
        const paragraphSpacing = 5; // Reduced from 6
        const paragraphCharSpace = 0.22;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(paragraphFontSize);
        doc.setTextColor(0, 0, 0);

        const renderParagraph = (text, extraSpacing = paragraphSpacing) => {
            const lines = doc.splitTextToSize(text, aboutMaxWidth);
            lines.forEach((line) => {
                doc.text(line, aboutMarginLeft, aboutY, { charSpace: paragraphCharSpace });
                aboutY += paragraphLineHeight;
            });
            aboutY += extraSpacing;
        };

        renderParagraph('When we hear about hacking events targeting corporate, government and even small business, we are grateful our organization is not the victim. Potential victims often possess the perception that hackers will only sensitive targets such as large corporations or banks. While some use their skills & talent for ethical purposes, others threaten, steal and cause harm. The size of an organization is irrelevant to an intruder; and you become an immediate target if you catch attention of malicious entities.');

        renderParagraph('CyberInsurify is a Cyber Insurance Analytics and Insurance Risk Management provider. CyberInsurify.com platform is a web-based user-friendly Cyber Risk & Incident management system designed to manage Cybersecurity and Cyber Risk posture. Our intuitive and innovative software is constantly evolving to meet customer and industry needs. We provide you and your business with the technology to implement, adapt and scale quickly, improve operational efficiencies, and exceed your customers expectations.', paragraphSpacing + 1);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('We are solving the', aboutMarginLeft, aboutY);
        const prefixWidth = doc.getTextWidth('We are solving the ');
        doc.setTextColor(0, 138, 255);
        doc.text('One of the biggest security questions companies face is,', aboutMarginLeft + prefixWidth, aboutY, { charSpace: paragraphCharSpace });
        aboutY += paragraphLineHeight + 4;

        doc.setTextColor(210, 0, 0);
        // Split long text to ensure it fits on the same page
        const riskQuestionText = 'What is my business Cyber Risk Exposure and the financial impact of this Cyber Risk?';
        const riskQuestionLines = doc.splitTextToSize(riskQuestionText, aboutMaxWidth);
        riskQuestionLines.forEach((line) => {
            doc.text(line, aboutMarginLeft, aboutY, { charSpace: paragraphCharSpace });
            aboutY += paragraphLineHeight;
        });
        aboutY += 6;

        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.text('Evidences Audit Report Details', pageWidth / 2, aboutY, { align: 'center' });
        aboutY += paragraphSpacing + 6;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(paragraphFontSize);
        renderParagraph('This report represents the valid and verified evidence provided by the customer for the evaluation of the implemented security controls. The NIST CyberSecurity Framework provides a common organizing structure for multiple approaches to cybersecurity by assembling standards, guidelines, and practices that are working effectively today.', paragraphSpacing + 2);

        const created = new Date();
        const createdStr = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}-${String(created.getDate()).padStart(2, '0')} ${String(created.getHours()).padStart(2, '0')}:${String(created.getMinutes()).padStart(2, '0')}:${String(created.getSeconds()).padStart(2, '0')}`;
        const assessmentOwner = ownerEmailValue;
        const scope = scopeValue;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        const metaLineGap = 6; // Reduced from 8

        doc.text('This report was created on : ', aboutMarginLeft, aboutY);
        doc.setFont('helvetica', 'bold');
        doc.text(createdStr, aboutMarginLeft + doc.getTextWidth('This report was created on : '), aboutY);
        aboutY += metaLineGap;

        doc.setFont('helvetica', 'normal');
        doc.text('This report provides overview of Verified Evidences ', aboutMarginLeft, aboutY);
        aboutY += metaLineGap;

        doc.text('The owner of this assessment is : ', aboutMarginLeft, aboutY);
        doc.setFont('helvetica', 'bold');
        doc.text(assessmentOwner, aboutMarginLeft + doc.getTextWidth('The owner of this assessment is : '), aboutY);
        aboutY += metaLineGap;

        doc.setFont('helvetica', 'normal');
        doc.text('The scope defined for this assessment is : ', aboutMarginLeft, aboutY);
        doc.setFont('helvetica', 'bold');
        doc.text(scope, aboutMarginLeft + doc.getTextWidth('The scope defined for this assessment is : '), aboutY);
        aboutY += metaLineGap;

        doc.setFont('helvetica', 'normal');
        doc.text('The evidences details are available in excel report ', aboutMarginLeft, aboutY);
        aboutY += metaLineGap;

        doc.text('*Download the excelsheet to view the evidences details ', aboutMarginLeft, aboutY);
        aboutY += metaLineGap + 2; // Reduced extra gap from 4

        renderParagraph('The results presented in this report are based on customer responses. For the purposes of this assessment, responses are considered valid and accurate. The process did not include document reviews, observations of work, or an examination of security controls in place to support the evaluated scope.', 0);

        // --- Page 4: Table of Contents ---
        doc.addPage();
        tocPageNumber = doc.internal.getNumberOfPages();
        doc.setPage(tocPageNumber);
        const tocMarginLeft = 22; // Reduced from 24
        const tocMarginRight = 22; // Reduced from 32
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(0, 0, 0);
        doc.text('Table of Contents', tocMarginLeft, 36);

        const drawTocLine = (item) => {
            // Check if we need a new page
            if (tocY > pageHeight - 30) {
                doc.addPage();
                tocY = 28; // Start from top of new page
            }

            const {
                label,
                page,
                bold = false,
                fontSize = bold ? 12 : 11,
                indent = 0,
                gap = 4.4,
                spacing = 10,
            } = item;

            const labelX = tocMarginLeft + indent;
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(fontSize);
            doc.text(label, labelX, tocY);

            const pageText = page ? String(page) : '';
            const pageWidthMeasure = doc.getTextWidth(pageText);
            const pageX = pageWidth - tocMarginRight - pageWidthMeasure;

            const labelWidth = doc.getTextWidth(label);
            const dotsStart = labelX + labelWidth + 4;
            const dotsEnd = pageX - 4;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9.5);
            for (let x = dotsStart; x < dotsEnd; x += gap) {
                doc.text('.', x, tocY);
            }

            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(fontSize);
            if (pageText) {
                doc.text(pageText, pageX, tocY);
            }

            tocY += spacing;
        };

        let tocY = 40; // Reduced from 56 to start higher

        const tocItems = (() => {
            const items = [];

            sectionOrder.forEach((sectionKey) => {
                const entries = sections[sectionKey] || [];
                if (!entries.length) return;

                items.push({
                    label: sectionConfig[sectionKey]?.title || sectionKey,
                    page: '',
                    spacing: 6.5,
                    bold: true,
                    sectionKey,
                });

                entries.forEach((entry, index) => {
                    items.push({
                        label: entry.name || `${sectionKey} ${index + 1}`,
                        page: '',
                        spacing: 5.2,
                        indent: 14,
                        sectionKey,
                        entryIndex: index,
                    });
                });

                if (items.length > 0) {
                    items[items.length - 1].spacing = 7; // Reduced from 10
                }
            });

            return items;
        })();

        const sectionPageMap = {};
        const entryPageMap = {};
        const getEntryKey = (sectionKey, index) => `${sectionKey}_${index}`;
        const recordSectionPage = (sectionKey, pageNumber) => {
            if (!sectionKey || sectionPageMap[sectionKey]) return;
            sectionPageMap[sectionKey] = pageNumber;
        };
        const recordEntryPage = (sectionKey, index, pageNumber) => {
            const key = getEntryKey(sectionKey, index);
            if (!entryPageMap[key]) {
                entryPageMap[key] = pageNumber;
            }
        };

        const addSectionIntro = (sectionKey, titleText, descriptionText) => {
            const margin = 22;
            const pageHeight = doc.internal.pageSize.getHeight();
            const sectionPageWidth = doc.internal.pageSize.getWidth();
            const usableWidth = sectionPageWidth - margin * 2;

            doc.addPage();
            recordSectionPage(sectionKey, doc.getNumberOfPages());

            let cursorY = 24;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            doc.text(titleText, margin, cursorY);
            cursorY += 12;

            if (descriptionText) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                const lines = doc.splitTextToSize(descriptionText, usableWidth);
                lines.forEach((line) => {
                    if (cursorY > pageHeight - margin) {
                        doc.addPage();
                        recordSectionPage(sectionKey, doc.getNumberOfPages());
                        cursorY = 24;
                    }
                    doc.text(line, margin, cursorY);
                    cursorY += 5.2;
                });
                cursorY += 4;
            }

            return {
                margin,
                cursorY,
                usableWidth,
                pageHeight,
            };
        };

        const renderSectionTables = (sectionKey) => {
            const entries = sections[sectionKey] || [];
            if (!entries.length) return;

            const config = sectionConfig[sectionKey] || {};
            const title = config.title || sectionKey;
            const description = contentMap?.[sectionKey]?.description || '';

            const intro = addSectionIntro(sectionKey, title, description);
            if (!intro) return;

            const { margin, usableWidth, pageHeight } = intro;
            let cursorY = intro.cursorY;

            const defaultBodyStyles = {
                font: 'helvetica',
                fontSize: 9,
                cellPadding: { top: 1.6, bottom: 1.6, left: 3, right: 2 },
                textColor: [0, 0, 0],
                lineColor: [120, 120, 120],
                lineWidth: 0.2,
            };

            const baseTableOptions = {
                styles: defaultBodyStyles,
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    cellPadding: 2,
                },
                margin: { left: margin, right: margin },
                tableLineColor: [120, 120, 120],
                tableLineWidth: 0.2,
            };

            const applyCellStyling = (data) => {
                if (data.section !== 'body') return;

                if (data.column.index === 0) {
                    data.cell.styles.fillColor = config.firstColumnFill || [230, 230, 230];
                    data.cell.styles.textColor = config.firstColumnText || [0, 0, 0];
                    data.cell.styles.fontStyle = 'bold';
                }

                if (data.column.index === 3) {
                    data.cell.styles.fillColor = config.priorityFill || [224, 224, 224];
                    data.cell.styles.textColor = config.priorityText || [0, 0, 0];
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.halign = 'center';
                }
            };

            entries.forEach((entry, index) => {
                const entryTitle = entry.name || `${title} ${index + 1}`;
                const entryHeading = (entry.heading || headingLookup[buildHeadingKey(sectionKey, entry.categoryCode)] || '').trim();
                const entryDescription = entry.description || contentMap?.[sectionKey]?.subtext || '';
                const normalizedDescription = entryDescription?.trim?.() || '';
                const shouldRenderDescription = normalizedDescription && normalizedDescription !== entryHeading;

                if (cursorY > pageHeight - 40) {
                    doc.addPage();
                    recordSectionPage(sectionKey, doc.getNumberOfPages());
                    cursorY = 24;
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(12);
                    doc.text(`${title} (cont.)`, margin, cursorY);
                    cursorY += 12;
                }

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                recordEntryPage(sectionKey, index, doc.getNumberOfPages());
                doc.text(entryTitle, margin, cursorY);
                cursorY += 6;

                if (entryHeading) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(10);
                    const headingLines = doc.splitTextToSize(entryHeading, usableWidth);
                    headingLines.forEach((line) => {
                        if (cursorY > pageHeight - margin) {
                            doc.addPage();
                            recordSectionPage(sectionKey, doc.getNumberOfPages());
                            cursorY = 24;
                            doc.setFont('helvetica', 'bold');
                            doc.setFontSize(11);
                            doc.text(`${entryTitle} (cont.)`, margin, cursorY);
                            cursorY += 6;
                            doc.setFont('helvetica', 'normal');
                            doc.setFontSize(10);
                        }
                        doc.text(line, margin, cursorY);
                        cursorY += 5.2;
                    });
                    cursorY += 4;
                }

                if (shouldRenderDescription) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(10);
                    const lines = doc.splitTextToSize(entryDescription, usableWidth);
                    lines.forEach((line) => {
                        if (cursorY > pageHeight - margin) {
                            doc.addPage();
                            recordSectionPage(sectionKey, doc.getNumberOfPages());
                            cursorY = 24;
                            doc.setFont('helvetica', 'bold');
                            doc.setFontSize(11);
                            doc.text(`${entryTitle} (cont.)`, margin, cursorY);
                            cursorY += 6;
                            doc.setFont('helvetica', 'normal');
                            doc.setFontSize(10);
                        }
                        doc.text(line, margin, cursorY);
                        cursorY += 5.2;
                    });
                    cursorY += 4;
                }

                const tableBody = (entry.rows || []).map((row) => {
                    const descriptionParts = [];
                    if (row.question) descriptionParts.push(row.question);
                    // Remarks are intentionally omitted from the PDF description per latest requirements.

                    const datesText = row.evidenceDates?.length ? row.evidenceDates.join(', ') : 'Not Available';
                    const verifiedText = row.verifiedStatuses?.length ? row.verifiedStatuses.join(', ') : 'Not Available';

                    return [
                        datesText,
                        row.categoryId || 'N/A',
                        descriptionParts.join('\n').trim() || 'Not Available',
                        verifiedText,
                    ];
                });

                if (!tableBody.length) {
                    return;
                }

                autoTable(doc, {
                    startY: cursorY,
                    head: [['Date', 'Function ID', 'Description', 'Verified']],
                    body: tableBody,
                    columnStyles: {
                        0: { cellWidth: 48 },
                        1: { cellWidth: 52 },
                        2: { cellWidth: usableWidth - 48 - 52 - 40 },
                        3: { cellWidth: 40, halign: 'center' },
                    },
                    alternateRowStyles: { fillColor: config.alternateFill || [240, 240, 240] },
                    didParseCell: applyCellStyling,
                    ...baseTableOptions,
                });

                cursorY = doc.lastAutoTable.finalY + 12;
            });
        };

        sectionOrder.forEach(renderSectionTables);

        console.log('Forcing Evidence TOC Items to Page: ' + tocPageNumber);
        doc.setPage(tocPageNumber);
        tocY = 40; // Reduced to match initial position
        tocItems.forEach((item) => {
            let pageNumber;
            if (item.sectionKey) {
                if (typeof item.entryIndex === 'number') {
                    pageNumber = entryPageMap[getEntryKey(item.sectionKey, item.entryIndex)];
                }
                if (!pageNumber) {
                    pageNumber = sectionPageMap[item.sectionKey];
                }
            } else {
                pageNumber = item.page;
            }

            const pageValue = pageNumber ? String(pageNumber) : '';
            drawTocLine({ ...item, page: pageValue });
        });
        doc.setPage(doc.getNumberOfPages());

        doc.save('Verified_Evidence.pdf');
        if (setShowEvidenceDropdown) setShowEvidenceDropdown(false);
        alert(i18n.t('msg.verifiedEvidencePdfDownloadedSuccess', { ns: 'framework_common', defaultValue: 'Verified Evidence PDF downloaded successfully!' }));
    } else {
        alert(i18n.t('msg.noControlDataAvailableForExport', { ns: 'framework_common', defaultValue: ' No control data available for export.' }));
        if (setShowEvidenceDropdown) setShowEvidenceDropdown(false);
    }
}